import { translate } from '../../i18n/index.js';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout.js';
import { Spinner, Tag } from '../../components/design/AtomFitUI.js';
import { adminApi } from '../../api/admin.api.js';
import { settingsApi, type StripeConnectStatus } from '../../api/settings.api.js';
import { useAuth } from '../../hooks/useAuth.js';

export function AdminSettingsPage() {
  const { role, tenantSlug, availableTenants } = useAuth();
  const isPlatform = role === 'PLATFORM_ADMIN';
  const isOperator = role === 'GYM_STAFF';
  const [tenantName, setTenantName] = useState('AtomFit');
  const [defaultCurrency, setDefaultCurrency] = useState('CZK');
  const [revenueSharePct, setRevenueSharePct] = useState('8');
  const [stripeStatus, setStripeStatus] = useState<StripeConnectStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openingStripe, setOpeningStripe] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  async function startStripeOnboarding() {
    setOpeningStripe(true);
    setMessage(null);
    try {
      const res = await settingsApi.stripeAccountLink();
      window.location.assign(res.data.url);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setMessage(
        msg ??
          translate(
            'pages.admin.admin_settings_page.start_stripe_onboarding.set_message.error_stripe_open',
          ),
      );
      setOpeningStripe(false);
    }
  }

  useEffect(() => {
    async function load() {
      setLoading(true);
      setMessage(null);
      setStripeStatus(null);
      const stripeState = searchParams.get('stripe');
      try {
        const [settings, stripe, platformTenants] = await Promise.all([
          settingsApi.get(),
          isOperator ? Promise.resolve({ data: null }) : settingsApi.stripeStatus(),
          isPlatform ? adminApi.listTenants(1, 100) : Promise.resolve(null),
        ]);
        const selectedTenantName =
          platformTenants?.data.data.find((tenant) => tenant.slug === tenantSlug)?.name ??
          availableTenants.find((item) => item.tenant.slug === tenantSlug)?.tenant.name ??
          tenantSlug ??
          'AtomFit';
        setTenantName(selectedTenantName);
        setDefaultCurrency(settings.data.defaultCurrency);
        setRevenueSharePct(String(settings.data.revenueSharePct));
        setStripeStatus(stripe.data);
        if (stripeState === 'return' && stripe.data) {
          setMessage(
            stripe.data.onboardingComplete
              ? translate('pages.admin.admin_settings_page.load.set_message.stripe')
              : translate('pages.admin.admin_settings_page.load.set_message.stripe_save_pending'),
          );
          setSearchParams({}, { replace: true });
        }
        if (stripeState === 'refresh' && !isOperator) {
          await startStripeOnboarding();
        }
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, [availableTenants, isOperator, isPlatform, searchParams, setSearchParams, tenantSlug]);

  async function saveSettings() {
    setSaving(true);
    setMessage(null);
    try {
      await settingsApi.update({
        defaultCurrency: defaultCurrency.toUpperCase(),
        ...(isPlatform ? { revenueSharePct: Number(revenueSharePct) } : {}),
      });
      setMessage(translate('pages.admin.admin_settings_page.save_settings.set_message.save'));
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setMessage(
        msg ?? translate('pages.admin.admin_settings_page.save_settings.set_message.error_save'),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminLayout
      title={translate('pages.admin.admin_settings_page.admin_settings_page.admin_layout.settings')}
      eyebrow={
        isPlatform
          ? translate('pages.admin.admin_settings_page.admin_settings_page.admin_layout.eyebrow')
          : isOperator
            ? translate(
                'pages.admin.admin_settings_page.admin_settings_page.admin_layout.eyebrow_2',
              )
            : translate(
                'pages.admin.admin_settings_page.admin_settings_page.admin_layout.eyebrow_3',
              )
      }
    >
      {loading ? (
        <div
          className="af-card"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: 24,
            color: 'var(--af-muted)',
          }}
        >
          <Spinner />{' '}
          {translate('pages.admin.admin_settings_page.admin_settings_page.div_card.loading')}
        </div>
      ) : (
        <div className="af-admin-main-grid">
          <section className="af-card" style={{ padding: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 18 }}>
              {translate('pages.admin.admin_settings_page.admin_settings_page.div.tenant')}
            </div>
            <div style={{ display: 'grid', gap: 14 }}>
              <label className="af-label">
                {translate('pages.admin.admin_settings_page.admin_settings_page.label_label.copy')}

                <input className="af-input" value={tenantName} disabled />
              </label>
              <label className="af-label">
                {translate(
                  'pages.admin.admin_settings_page.admin_settings_page.label_label.copy_2',
                )}

                <input className="af-input af-mono" value={tenantSlug ?? ''} disabled />
              </label>
              {!isOperator && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <label className="af-label">
                    {translate(
                      'pages.admin.admin_settings_page.admin_settings_page.label_label.currency',
                    )}

                    <input
                      className="af-input"
                      value={defaultCurrency}
                      maxLength={3}
                      onChange={(e) => setDefaultCurrency(e.target.value.toUpperCase())}
                    />
                  </label>
                  <label className="af-label">
                    {translate(
                      'pages.admin.admin_settings_page.admin_settings_page.label_label.pricing',
                    )}

                    <input
                      className="af-input"
                      type="number"
                      min={0}
                      max={100}
                      value={revenueSharePct}
                      onChange={(e) => setRevenueSharePct(e.target.value)}
                      disabled={!isPlatform}
                    />
                  </label>
                </div>
              )}
              {message && (
                <div
                  className={message.includes('uloženo') ? '' : 'af-error'}
                  style={
                    message.includes('uloženo')
                      ? {
                          padding: '10px 14px',
                          borderRadius: 8,
                          background: 'rgba(74,222,128,0.1)',
                          color: 'var(--af-success)',
                          fontSize: 13,
                        }
                      : undefined
                  }
                >
                  {message}
                </div>
              )}
              {!isOperator && (
                <button
                  className="af-primary-btn"
                  style={{ padding: '12px 18px', fontSize: 13 }}
                  disabled={saving}
                  onClick={() => void saveSettings()}
                >
                  {saving ? (
                    <Spinner />
                  ) : (
                    translate(
                      'pages.admin.admin_settings_page.admin_settings_page.button_primary_btn.save',
                    )
                  )}
                </button>
              )}
            </div>
          </section>

          {!isOperator && (
            <section className="af-card" style={{ padding: 24 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  marginBottom: 18,
                }}
              >
                <div style={{ fontSize: 16, fontWeight: 800 }}>
                  {translate('pages.admin.admin_settings_page.admin_settings_page.div.stripe')}
                </div>
                {stripeStatus?.onboardingComplete ? (
                  <Tag color="var(--af-success)" bg="rgba(74,222,128,0.1)">
                    {translate('pages.admin.admin_settings_page.admin_settings_page.tag.copy')}
                  </Tag>
                ) : (
                  <Tag color="var(--af-peak)" bg="var(--af-peak-soft)">
                    {translate('pages.admin.admin_settings_page.admin_settings_page.tag.copy_2')}
                  </Tag>
                )}
              </div>
              {stripeStatus?.migrationRequired && (
                <p role="status">{translate('stripe.migration_required')}</p>
              )}
              <div style={{ display: 'grid', gap: 10, marginBottom: 18 }}>
                <StatusRow
                  label={translate(
                    'pages.admin.admin_settings_page.admin_settings_page.status_row.stripe',
                  )}
                  value={
                    stripeStatus?.stripeAccountId ??
                    translate(
                      'pages.admin.admin_settings_page.admin_settings_page.status_row.value',
                    )
                  }
                />

                <StatusRow
                  label={translate(
                    'pages.admin.admin_settings_page.admin_settings_page.status_row.label',
                  )}
                  value={
                    stripeStatus?.chargesEnabled
                      ? translate(
                          'pages.admin.admin_settings_page.admin_settings_page.status_row.value_2',
                        )
                      : translate(
                          'pages.admin.admin_settings_page.admin_settings_page.status_row.value_3',
                        )
                  }
                />

                <StatusRow
                  label={translate(
                    'pages.admin.admin_settings_page.admin_settings_page.status_row.label_2',
                  )}
                  value={
                    stripeStatus?.payoutsEnabled
                      ? translate(
                          'pages.admin.admin_settings_page.admin_settings_page.status_row.value_4',
                        )
                      : translate(
                          'pages.admin.admin_settings_page.admin_settings_page.status_row.value_5',
                        )
                  }
                />

                <StatusRow
                  label={translate(
                    'pages.admin.admin_settings_page.admin_settings_page.status_row.details',
                  )}
                  value={
                    stripeStatus?.detailsSubmitted
                      ? translate(
                          'pages.admin.admin_settings_page.admin_settings_page.status_row.value_6',
                        )
                      : translate(
                          'pages.admin.admin_settings_page.admin_settings_page.status_row.value_7',
                        )
                  }
                />

                {stripeStatus?.disabledReason && (
                  <StatusRow
                    label={translate(
                      'pages.admin.admin_settings_page.admin_settings_page.status_row.reason',
                    )}
                    value={stripeStatus.disabledReason}
                  />
                )}
                {stripeStatus?.requirementsDue.length ? (
                  <StatusRow
                    label={translate(
                      'pages.admin.admin_settings_page.admin_settings_page.status_row.label_3',
                    )}
                    value={`${stripeStatus.requirementsDue.length} položek čeká`}
                  />
                ) : null}
              </div>
              {!stripeStatus?.onboardingComplete && (
                <button
                  className="af-outline-btn"
                  style={{ padding: '12px 18px' }}
                  disabled={openingStripe}
                  onClick={() => void startStripeOnboarding()}
                >
                  {openingStripe ? (
                    <Spinner />
                  ) : stripeStatus?.connected ? (
                    translate(
                      'pages.admin.admin_settings_page.admin_settings_page.button_outline_btn.label',
                    )
                  ) : (
                    translate(
                      'pages.admin.admin_settings_page.admin_settings_page.button_outline_btn.open',
                    )
                  )}
                </button>
              )}
            </section>
          )}
        </div>
      )}
    </AdminLayout>
  );
}

function StatusRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12,
        padding: '10px 12px',
        background: 'var(--af-s2)',
        border: '1px solid var(--af-border)',
        borderRadius: 8,
      }}
    >
      <span style={{ color: 'var(--af-muted)', fontSize: 13 }}>{label}</span>
      <span
        className="af-mono"
        style={{
          fontSize: 12,
          color: 'var(--af-text)',
          textAlign: 'right',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </span>
    </div>
  );
}
